import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { InstagramIcon } from '@/shared/components/ui/InstagramIcon';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { getCurrentAccessToken } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRightLeft,
  BadgeCheck,
  Check,
  Copy,
  Loader2,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plug,
  Plus,
  Power,
  QrCode,
  RotateCw,
  Settings2,
  Trash2,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChannelGeneralSettingsModal } from './ChannelGeneralSettingsModal';
import { QrPairingView } from './QrPairingView';
import {
  type WhatsappNumber,
  connectDialog360,
  connectManual,
  connectWame,
  convertQrChannel,
  createQrChannel,
  deleteQrChannel,
  disconnectNumber,
  getPlanUsage,
  getWebhookInfo,
  listChannels,
  migrateChannelToQr,
  purgeMetaChannel,
  purgeQrChannel,
  registerMigrationIntent,
  renameChannel,
  setNumberStatus,
  updateChannelCredentials,
} from './api';
import {
  EmbeddedSignupCancelledError,
  EmbeddedSignupConfigError,
  openMetaConnector,
} from './fb-sdk';
import { InstagramLoginCancelledError, openInstagramLogin } from './instagram-login';

type ChannelKind =
  | 'whatsapp_web'
  | 'whatsapp_web_v2'
  | 'whatsapp_business_api'
  | 'cloud_dev_app'
  | 'dialog360'
  | 'instagram_wame'
  | 'instagram_meta';

export function ChannelsPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showDialog360Modal, setShowDialog360Modal] = useState(false);
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrProvider, setQrProvider] = useState<'whatsmeow' | 'zapo'>('whatsmeow');
  /**
   * Nome do canal digitado no AddChannelModal. Usado pra preencher o `label`
   * no QR (na criação) e pra fazer auto-rename pós-conexão no Manual e Embedded
   * Signup (que retornam whatsapp_number_id, daí PATCH /rename).
   */
  const [pendingChannelName, setPendingChannelName] = useState('');
  const [embeddedError, setEmbeddedError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['channels'],
    queryFn: listChannels,
  });

  // Uso vs limite do plano — barra o "Adicionar Canal" com toast quando o
  // limite já foi atingido (o gate no backend segue como autoridade).
  // Mudança de limite no Super chega via `tenant.flags_changed` (invalida a
  // query na hora) — staleTime longo só economiza refetch em navegação.
  const planUsageQuery = useQuery({
    queryKey: ['plan-usage'],
    queryFn: getPlanUsage,
    staleTime: 300_000,
  });

  const embeddedMut = useMutation({
    mutationFn: async () => {
      // Caminho preferencial: Conector hosted em meta.chatplusbr.com — funciona
      // em qualquer ambiente (localhost, prod) sem cadastrar domínio do app
      // principal nos "Domínios permitidos" do Meta. Só o domínio do Conector
      // precisa estar lá.
      const token = getCurrentAccessToken();
      if (!token) {
        throw new EmbeddedSignupConfigError('Sessão expirada. Faça login novamente.');
      }
      return openMetaConnector(token);
    },
    onSuccess: async (result) => {
      const name = pendingChannelName.trim();
      if (name) {
        try {
          await renameChannel(result.whatsapp_number_id, name);
        } catch {
          // rename é best-effort — canal conecta mesmo sem label custom
        }
      }
      setPendingChannelName('');
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      setEmbeddedError(null);
    },
    onError: (err) => {
      if (err instanceof EmbeddedSignupCancelledError) {
        setEmbeddedError(null); // cancelar é silencioso
        return;
      }
      if (err instanceof EmbeddedSignupConfigError) {
        setEmbeddedError(err.message);
        return;
      }
      if (err instanceof ApiError) {
        setEmbeddedError(err.problem.title);
        return;
      }
      setEmbeddedError('Não foi possível conectar. Tente novamente em instantes.');
    },
  });

  // Instagram Direct pela API oficial: abre o login do Instagram direto (sem
  // tela intermediária), igual ao WhatsApp Business API. O nome do canal vai
  // como argumento — o estado `pendingChannelName` ainda não atualizou quando
  // o mutate dispara.
  const instagramLoginMut = useMutation({
    mutationFn: async (nome: string) => {
      const token = getCurrentAccessToken();
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');
      return openInstagramLogin(token, nome.trim() || 'Instagram');
    },
    onSuccess: (out) => {
      setPendingChannelName('');
      setEmbeddedError(null);
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      if (out.status === 'connected') {
        toast.success(`Instagram @${out.result.username} conectado!`, {
          description: 'Mande um Direct pra conta pra confirmar que as mensagens estão chegando.',
        });
      } else {
        // Janela fechou sem avisar: a lista recarrega — se o login terminou,
        // o canal aparece nela.
        toast.info('Janela do Instagram fechada', {
          description: 'Se você concluiu o login, o canal já aparece na lista.',
        });
      }
    },
    onError: (err) => {
      setPendingChannelName('');
      if (err instanceof InstagramLoginCancelledError) {
        setEmbeddedError(null); // cancelar é silencioso
        return;
      }
      setEmbeddedError(
        err instanceof Error ? err.message : 'Não foi possível conectar o Instagram.',
      );
    },
  });

  // Listagem flat — todos os canais lado a lado independente do tipo.
  // Ícone do card diferencia (BadgeCheck pra meta_official, QrCode pra qr_code).
  const channels = data ?? [];

  return (
    <div className="flex-1 overflow-y-auto bg-n-50 px-5 py-6">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="font-bold text-[18px] text-n-900">Canais WhatsApp</h1>
          <p className="text-[12px] text-n-500">
            Conecte e gerencie os números do seu negócio. Tudo via Cloud API oficial Meta.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              const u = planUsageQuery.data;
              if (u && u.max_channels != null && u.channels_active >= u.max_channels) {
                toast.error(
                  `Limite de ${u.max_channels} ${u.max_channels === 1 ? 'canal' : 'canais'} do plano atingido`,
                  { description: 'Fale com o suporte para ampliar o plano.' },
                );
                return;
              }
              setShowAddModal(true);
            }}
            disabled={embeddedMut.isPending || instagramLoginMut.isPending}
          >
            {embeddedMut.isPending || instagramLoginMut.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Plus strokeWidth={2} aria-hidden="true" />
            )}
            Adicionar Canal
          </Button>
        </div>
      </div>

      {embeddedError && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-4 py-3 text-[13px] text-warning-700"
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>{embeddedError}</strong>
            <button
              type="button"
              onClick={() => {
                setEmbeddedError(null);
                setShowAddModal(true);
              }}
              className="ml-2 underline"
            >
              Tentar outro tipo
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="rounded-md border border-n-200 bg-n-0 p-12 text-center text-sm text-n-500">
          Carregando canais...
        </div>
      )}

      {!isLoading && channels.length === 0 && (
        <div className="rounded-md border border-dashed border-n-300 bg-n-0 p-12 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-pill bg-brand-50 text-brand-600">
            <Plug size={24} />
          </div>
          <h2 className="mt-3 font-bold text-[15px] text-n-800">Nenhum canal conectado ainda</h2>
          <p className="mt-1 text-[13px] text-n-500">
            Comece conectando o primeiro número WhatsApp do seu negócio.
          </p>
        </div>
      )}

      {!isLoading && channels.length > 0 && (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(380px,1fr))]">
          {channels.map((channel) => (
            <ChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddChannelModal
          officialOnly={planUsageQuery.data?.official_only === true}
          onClose={() => setShowAddModal(false)}
          onConfirm={(name, kind) => {
            setPendingChannelName(name);
            setShowAddModal(false);
            if (kind === 'whatsapp_web' || kind === 'whatsapp_web_v2') {
              setQrProvider(kind === 'whatsapp_web_v2' ? 'zapo' : 'whatsmeow');
              setShowQrModal(true);
            } else if (kind === 'whatsapp_business_api') {
              embeddedMut.mutate();
            } else if (kind === 'dialog360') {
              setShowDialog360Modal(true);
            } else if (kind === 'instagram_wame') {
              setShowInstagramModal(true);
            } else if (kind === 'instagram_meta') {
              instagramLoginMut.mutate(name);
            } else {
              setShowManualModal(true);
            }
          }}
        />
      )}
      {showManualModal && (
        <ManualConnectModal
          initialName={pendingChannelName}
          onClose={() => {
            setShowManualModal(false);
            setPendingChannelName('');
          }}
        />
      )}
      {showDialog360Modal && (
        <Dialog360ConnectModal
          initialName={pendingChannelName}
          onClose={() => {
            setShowDialog360Modal(false);
            setPendingChannelName('');
          }}
        />
      )}
      {showInstagramModal && (
        <InstagramConnectModal
          initialName={pendingChannelName}
          onClose={() => {
            setShowInstagramModal(false);
            setPendingChannelName('');
          }}
        />
      )}
      {showQrModal && (
        <QrConnectModal
          initialName={pendingChannelName}
          provider={qrProvider}
          onClose={() => {
            setShowQrModal(false);
            setPendingChannelName('');
          }}
        />
      )}
    </div>
  );
}

const QUALITY_STYLE: Record<'GREEN' | 'YELLOW' | 'RED', string> = {
  GREEN: 'bg-success-50 text-success-700',
  YELLOW: 'bg-warning-50 text-warning-700',
  RED: 'bg-danger-50 text-danger-600',
};

function ChannelCard({ channel }: { channel: WhatsappNumber }) {
  // QR code (whatsmeow, não-oficial) tem campos e ações totalmente diferentes
  // do canal Meta oficial — sem qualidade/limite/origem, sem inativar/reativar.
  // Em vez de espalhar `if channelType === 'qr_code'` no JSX inteiro, delega
  // pro componente dedicado QrChannelCard.
  if (channel.channelType === 'qr_code') {
    return <QrChannelCard channel={channel} />;
  }
  if (channel.channelType === 'instagram') {
    return <InstagramChannelCard channel={channel} />;
  }

  const queryClient = useQueryClient();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const isVerifying = channel.status === 'pending' || channel.status === 'unregistered';
  const isInactive = channel.status === 'inactive';
  const isRevoked = channel.accountStatus === 'revoked' || channel.accountStatus === 'expired';
  const isManual = channel.createdVia === 'manual';

  // Inativar/Reativar — usado APENAS no canal manual (PATCH status). Não toca em Meta.
  const statusMut = useMutation({
    mutationFn: (status: 'active' | 'inactive') => setNumberStatus(channel.id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  // Desconecta o canal (APENAS Embedded) — chama Meta (deregister + revoke +
  // unsubscribe, best-effort) e apaga credenciais locais. Preserva tickets/messages
  // → Reconectar reusa a row.
  const disconnectMut = useMutation({
    mutationFn: () => disconnectNumber(channel.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  // Reconecta canal Embedded previamente desconectado — abre o Conector
  // passando `channel.id` como target. Backend força UPDATE da row (preserva
  // ID + histórico), troca phone/WABA/token. Cliente pode escolher QUALQUER
  // WABA/número — mistura de histórico aceita (lição #95).
  const reconnectMut = useMutation({
    mutationFn: async () => {
      const token = getCurrentAccessToken();
      if (!token) {
        throw new EmbeddedSignupConfigError('Sessão expirada. Faça login novamente.');
      }
      return openMetaConnector(token, channel.id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  // Migração Cloud→QR (preserva histórico): vira o canal pra Qr Code API V2,
  // limpa credenciais Meta (com proteção pra WABA compartilhada) e abre o
  // pareamento. Motor whatsmeow disponível depois via Converter, se quiser.
  const migrateToQrMut = useMutation({
    mutationFn: () => migrateChannelToQr(channel.id, 'zapo'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      toast.success('Canal migrado para QR Code', {
        description: 'Histórico preservado. Clique em "Gerar QR Code" no card pra parear o número.',
      });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Erro ao migrar o canal');
      }
    },
  });
  const handleMigrateToQr = async (): Promise<void> => {
    const ok = await confirm({
      title: `Migrar ${channel.displayName ?? `canal #${channel.id}`} para QR Code?`,
      description:
        'O canal vira conexão QR (Qr Code API V2) preservando TODO o histórico de atendimento. A conexão com a Meta será desfeita (credenciais removidas) e um novo QR Code será gerado pra parear o número no celular.',
      confirmText: 'Migrar para QR',
      variant: 'danger',
    });
    if (ok) migrateToQrMut.mutate();
  };

  // Excluir permanentemente — apaga TODO o histórico de mensagens via worker.
  // Espelha purgeQrChannel pro canal Cloud API. Contatos preservados.
  const purgeMut = useMutation({
    mutationFn: () => purgeMetaChannel(channel.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  return (
    <article className="flex flex-col gap-3.5 rounded-md border border-n-200 bg-n-0 p-5 shadow-sm">
      <header className="flex items-center gap-3.5">
        <div
          className={cn(
            'flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-md',
            isVerifying || isInactive ? 'bg-n-300' : 'bg-wa-500',
          )}
          title="Canal oficial Meta Cloud API"
        >
          <BadgeCheck size={26} strokeWidth={1.75} className="text-white" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-[15px] font-semibold text-n-900">
              {channel.displayName ?? `Canal · ${(channel.wabaId ?? '').slice(-6)}`}
            </div>
            <button
              type="button"
              onClick={() => setShowRenameModal(true)}
              className="text-n-400 hover:text-n-700"
              title="Renomear canal"
              aria-label="Renomear canal"
            >
              <Pencil size={13} />
            </button>
            <span
              className="rounded-md bg-success-50 px-1.5 py-0.5 text-[10px] font-semibold text-success-700"
              title="WhatsApp Business Cloud API oficial Meta"
            >
              Oficial
            </span>
          </div>
          <div className="mt-0.5 font-mono text-xs text-n-500">
            <span
              className="mr-1.5 rounded bg-n-100 px-1.5 py-0.5 text-[11px] text-n-500"
              title="ID do Canal"
            >
              #{channel.id}
            </span>
            {channel.displayNumber ??
              (channel.status === 'pending_qr' ? 'aguardando pareamento' : '—')}
          </div>
        </div>
        <StatusChip status={channel.status} />
      </header>

      {showRenameModal && (
        <ChannelGeneralSettingsModal channel={channel} onClose={() => setShowRenameModal(false)} />
      )}

      {showSettingsModal && (
        <ChannelSettingsModal channel={channel} onClose={() => setShowSettingsModal(false)} />
      )}

      {isRevoked && (
        <div className="flex items-center gap-1.5 rounded-md bg-danger-50 px-2.5 py-1.5 text-[11px] text-danger-700">
          <AlertTriangle size={12} />
          Token Meta revogado — reconecte pra continuar enviando.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 border-t border-n-200 pt-3.5">
        {isVerifying ? (
          <>
            <Stat
              label="Status Meta"
              value="Aguardando"
              valueClassName="text-warning-700 text-[13px]"
            />
            <Stat
              label="PIN 2FA"
              value="Configurado"
              valueClassName="text-success-600 text-[13px]"
            />
            <Stat label="Webhook" value="—" valueClassName="text-n-500 text-[13px]" />
          </>
        ) : (
          <>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-n-500">
                Qualidade
              </span>
              <div className="mt-1">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.05em]',
                    QUALITY_STYLE[channel.qualityRating ?? 'GREEN'],
                  )}
                >
                  {channel.qualityRating ?? '—'}
                </span>
              </div>
            </div>
            <Stat label="Limite/dia" value={tierLabel(channel.messagingTier)} />
            <Stat
              label="Origem"
              value={
                channel.bsp === 'dialog360'
                  ? '360dialog'
                  : channel.createdVia === 'manual'
                    ? 'API Cloud Dev'
                    : channel.coexistenceMode
                      ? 'Coexistência'
                      : 'API Cloud'
              }
            />
          </>
        )}
      </div>

      <footer className="flex gap-2 border-t border-n-200 pt-3.5">
        {isManual ? (
          // ─── Canal MANUAL — Inativar/Reativar + Settings + Lixeira ───
          // Admin gerencia token+PIN direto no BM dele. Inativar é simples
          // (PATCH status), NÃO toca em Meta. Lixeira faz purge permanente.
          <>
            {isInactive ? (
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => statusMut.mutate('active')}
                disabled={statusMut.isPending}
              >
                <PlayCircle aria-hidden="true" />
                Reativar
              </Button>
            ) : (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => statusMut.mutate('inactive')}
                disabled={statusMut.isPending || isVerifying}
                title="Pausa o recebimento de mensagens sem desconectar do Meta"
              >
                <PauseCircle aria-hidden="true" />
                Inativar
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowSettingsModal(true)}
              title="Configurações do canal (webhook, credenciais)"
              aria-label="Configurações"
            >
              <Settings2 aria-hidden="true" />
            </Button>
          </>
        ) : // ─── Canal EMBEDDED — Desconectar/Reconectar (Meta) ───
        isInactive ? (
          <Button
            variant="primary"
            className="flex-1"
            onClick={() => reconnectMut.mutate()}
            disabled={reconnectMut.isPending}
            title="Reconectar via Facebook (reusa a mesma fila)"
          >
            <Plug aria-hidden="true" />
            Reconectar
          </Button>
        ) : (
          <Button
            variant="outline"
            className="flex-1"
            onClick={async () => {
              const ok = await confirm({
                title: `Desconectar ${channel.displayName ?? `canal #${channel.id}`}?`,
                description:
                  'Desconecta o canal do WhatsApp. Pra reconectar, o cliente vai precisar de um código SMS no celular. Histórico de mensagens, tickets e contatos são preservados.',
                confirmText: 'Desconectar',
                variant: 'danger',
              });
              if (ok) disconnectMut.mutate();
            }}
            disabled={disconnectMut.isPending || isVerifying}
            title="Desconecta este canal do WhatsApp (preserva histórico)"
          >
            <Power aria-hidden="true" />
            Desconectar
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => void handleMigrateToQr()}
          disabled={migrateToQrMut.isPending}
          aria-label="Migrar para QR Code"
          title="Migrar para QR Code"
        >
          <QrCode size={14} />
        </Button>
        <Button
          variant="ghost"
          onClick={async () => {
            const ok = await confirm({
              title: `Excluir ${channel.displayName ?? `canal #${channel.id}`}?`,
              description:
                'Desconecta do WhatsApp e remove o canal + TODO o histórico de mensagens permanentemente. Contatos são preservados. Esta ação não pode ser desfeita.',
              confirmText: 'Excluir definitivamente',
              variant: 'danger',
              requireText: 'EXCLUIR',
            });
            if (ok) purgeMut.mutate();
          }}
          disabled={purgeMut.isPending}
          aria-label="Excluir canal definitivamente"
          title="Excluir canal e histórico de mensagens"
        >
          <Trash2 size={14} className="text-danger-500" />
        </Button>
      </footer>
    </article>
  );
}

/**
 * Card específico pra canais qr_code (whatsmeow/não-oficial).
 *
 * Diferenças vs Meta oficial:
 * - SEM qualidade, limite/dia, origem (não fazem sentido em canal QR).
 * - Status enxuto: "Conectado em <data>" se active, "Aguardando QR" caso contrário.
 * - Footer condicional: conectado → desconectar; desconectado → gerar QR + excluir.
 */
function QrChannelCard({ channel }: { channel: WhatsappNumber }) {
  const [showPairModal, setShowPairModal] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const queryClient = useQueryClient();
  const isConnected = channel.status === 'active';
  const isWaitingPair =
    channel.status === 'pending_qr' ||
    channel.status === 'unregistered' ||
    channel.status === 'pending';

  // Bifurca rota de exclusão por channelType — qr_code (whatsmeow) usa
  // DELETE /whatsapp-numbers/:id/qr-code (faz logout via fila e marca revoked).
  // meta_official usa DELETE /whatsapp-numbers/:id (Cloud API + unsubscribe Meta).
  const disconnectMut = useMutation({
    mutationFn: () =>
      channel.channelType === 'qr_code'
        ? deleteQrChannel(channel.id)
        : disconnectNumber(channel.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  // Forçar restart preserva auth state — abre modal de pareamento em modo
  // restart (QrPairingView com mode='restart' chama /qr-code/restart no mount,
  // escuta eventos do socket, mostra "Conectado!" e fecha automaticamente).

  // Exclusão permanente: marca deleted_at + enfileira purge no worker.
  // UI esconde card imediato; worker apaga messages/tickets/sessions em batches.
  const purgeMut = useMutation({
    mutationFn: () => purgeQrChannel(channel.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  // Conversão de motor (whatsmeow ↔ zapo): histórico preservado, pareamento
  // não migra — desvincula o device atual e abre QR fresh no motor novo.
  const targetProvider = channel.provider === 'zapo' ? 'whatsmeow' : 'zapo';
  const targetLabel = targetProvider === 'zapo' ? 'Qr Code API V2' : 'Qr Code API';
  const convertMut = useMutation({
    mutationFn: () => convertQrChannel(channel.id, targetProvider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      setShowPairModal(true); // já abre o QR do motor novo
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Erro ao converter o canal');
      }
    },
  });
  const handleConvert = async (): Promise<void> => {
    const ok = await confirm({
      title: `Converter para ${targetLabel}?`,
      description:
        'Todo o histórico de conversas é preservado. O WhatsApp será desconectado e um novo QR Code será gerado para parear no tipo novo.',
      confirmText: 'Converter',
    });
    if (ok) convertMut.mutate();
  };
  const convertButton = (
    <Button
      variant="outline"
      onClick={() => void handleConvert()}
      disabled={convertMut.isPending}
      aria-label={`Converter para ${targetLabel}`}
      title={`Converter para ${targetLabel}`}
    >
      <ArrowRightLeft size={14} />
    </Button>
  );

  // Migração QR→API Oficial (preserva histórico): abre o Embedded Signup com
  // target_channel_id + intenção pré-registrada. Número diferente do pareado
  // é permitido sem confirmação extra (decisão do dono).
  const finishCloudMigration = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['channels'] });
    void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    toast.success('Canal migrado para a API Oficial', {
      description: 'Histórico preservado. O pareamento QR anterior foi desfeito.',
    });
  };
  const migrateToCloudMut = useMutation({
    mutationFn: async () => {
      const token = getCurrentAccessToken();
      if (!token) {
        throw new EmbeddedSignupConfigError('Sessão expirada. Faça login novamente.');
      }
      // Intent no backend ANTES do popup — o conector hospedado antigo não
      // repassa a flag `migrate`; a guarda TARGET_IS_QR consome do Redis.
      await registerMigrationIntent(channel.id);
      return openMetaConnector(token, channel.id, { migrate: true });
    },
    onSuccess: finishCloudMigration,
    onError: (err) => {
      if (err instanceof EmbeddedSignupCancelledError) return; // fechou o popup
      if (err instanceof EmbeddedSignupConfigError) {
        toast.error('Migração não concluída', { description: err.message });
        return;
      }
      // biome-ignore lint/suspicious/noConsole: diagnóstico de erro inesperado
      console.error('[migrar-oficial] erro inesperado:', err);
      toast.error('Erro ao migrar o canal', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });
  const handleMigrateToCloud = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Migrar para a API Oficial da Meta?',
      description:
        'O canal vira API oficial (Cloud API) preservando TODO o histórico. Você vai conectar o número via Facebook; o pareamento QR atual será desfeito ao concluir.',
      confirmText: 'Migrar',
    });
    if (ok) migrateToCloudMut.mutate();
  };
  const migrateButton = (
    <Button
      variant="outline"
      onClick={() => void handleMigrateToCloud()}
      disabled={migrateToCloudMut.isPending}
      aria-label="Migrar para a API Oficial da Meta"
      title="Migrar para a API Oficial da Meta"
    >
      <BadgeCheck size={14} />
    </Button>
  );

  const isZapo = channel.provider === 'zapo';
  const providerLabel = isZapo ? 'Qr Code API V2' : 'Qr Code API';

  return (
    <article className="flex flex-col gap-3.5 rounded-md border border-n-200 bg-n-0 p-5 shadow-sm">
      <header className="flex items-center gap-3.5">
        {/* Identidade visual por motor: V1 = QR âmbar; V2 = raio azul (Zap ↔ zapo). */}
        <div
          className={cn(
            'flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-md',
            isConnected ? (isZapo ? 'bg-brand-500' : 'bg-warning-500') : 'bg-n-300',
          )}
          title={providerLabel}
        >
          {isZapo ? (
            <Zap size={26} strokeWidth={1.75} className="text-white" aria-hidden="true" />
          ) : (
            <QrCode size={26} strokeWidth={1.75} className="text-white" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-[15px] font-semibold text-n-900">
              {channel.displayName ?? `Canal · ${channel.id}`}
            </div>
            <button
              type="button"
              onClick={() => setShowRenameModal(true)}
              className="text-n-400 hover:text-n-700"
              title="Renomear canal"
              aria-label="Renomear canal"
            >
              <Pencil size={13} />
            </button>
            <span className="rounded-md bg-warning-50 px-1.5 py-0.5 text-[10px] font-semibold text-warning-700">
              Não-oficial
            </span>
          </div>
          <div className="mt-0.5 font-mono text-xs text-n-500">
            <span
              className="mr-1.5 rounded bg-n-100 px-1.5 py-0.5 text-[11px] text-n-500"
              title="ID do Canal"
            >
              #{channel.id}
            </span>
            {channel.displayNumber ?? 'aguardando pareamento'}
          </div>
        </div>
        <StatusChip status={channel.status} />
      </header>

      {/* Linha de status com ícone + texto + última atualização —
          mesma altura visual do card oficial (grid 3 cols) pra alinhar botões. */}
      <div className="flex items-center gap-3 border-t border-n-200 py-3.5">
        {isConnected ? (
          <Wifi
            size={28}
            strokeWidth={2.25}
            className="shrink-0 text-success-600"
            aria-hidden="true"
          />
        ) : (
          <WifiOff
            size={28}
            strokeWidth={2.25}
            className="shrink-0 text-danger-600"
            aria-hidden="true"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className={cn('font-medium text-[14px]', isConnected ? 'text-n-900' : 'text-n-900')}>
            {isConnected
              ? 'Conexão estabelecida!'
              : 'Falha ao iniciar comunicação para este canal.'}
          </div>
          {/* Uma linha só, com o carimbo do estado atual: conectado → última
              conexão; desconectado → quando desconectou; qualquer outro caso
              (gerar QR, canal recém-criado) → última mudança do canal. */}
          <div className="mt-0.5 text-[12px] text-n-500">
            Última atualização: {(() => {
              const ts = isConnected
                ? (channel.lastConnectedAt ?? channel.registeredAt ?? channel.updatedAt)
                : channel.status === 'revoked'
                  ? (channel.disconnectedAt ?? channel.updatedAt)
                  : channel.updatedAt;
              return ts ? formatDateTime(ts) : '—';
            })()}
          </div>
        </div>
      </div>

      <footer className="flex gap-2 border-t border-n-200 pt-3.5">
        {isConnected ? (
          <>
            <Button
              variant="outline"
              className="flex-1"
              onClick={async () => {
                const ok = await confirm({
                  title: `Desconectar ${channel.displayNumber ?? channel.displayName}?`,
                  description: 'O canal volta pro estado "aguardando QR".',
                  confirmText: 'Desconectar',
                  variant: 'danger',
                });
                if (ok) disconnectMut.mutate();
              }}
              disabled={disconnectMut.isPending}
            >
              <Power aria-hidden="true" />
              Desconectar
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowRestartModal(true)}
              aria-label="Forçar restart do canal"
              title="Forçar restart do canal (preserva identidade)"
            >
              <RotateCw size={14} />
            </Button>
            {convertButton}
            {migrateButton}
            <Button
              variant="ghost"
              onClick={async () => {
                const ok = await confirm({
                  title: `Excluir ${channel.displayName ?? `canal #${channel.id}`}?`,
                  description:
                    'Desconecta do WhatsApp e remove o canal + TODO o histórico de mensagens permanentemente. Contatos são preservados. Esta ação não pode ser desfeita.',
                  confirmText: 'Excluir definitivamente',
                  variant: 'danger',
                  requireText: 'EXCLUIR',
                });
                if (ok) purgeMut.mutate();
              }}
              disabled={purgeMut.isPending}
              aria-label="Excluir canal definitivamente"
              title="Excluir canal e histórico de mensagens"
            >
              <Trash2 size={14} className="text-danger-500" />
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" className="flex-1" onClick={() => setShowPairModal(true)}>
              <QrCode aria-hidden="true" />
              Gerar QR Code
            </Button>
            {/* Sem botão Reiniciar aqui: canal desconectado não tem identidade
                salva pra reconectar (Desconectar desvincula e limpa o vault) —
                o caminho é Gerar QR Code. */}
            {convertButton}
            {migrateButton}
            <Button
              variant="ghost"
              onClick={async () => {
                const ok = await confirm({
                  title: `Excluir ${channel.displayName ?? `canal #${channel.id}`}?`,
                  description:
                    'Remove o canal e TODO o histórico de mensagens permanentemente. Contatos são preservados. Esta ação não pode ser desfeita.',
                  confirmText: 'Excluir definitivamente',
                  variant: 'danger',
                  requireText: 'EXCLUIR',
                });
                if (ok) purgeMut.mutate();
              }}
              disabled={purgeMut.isPending}
              aria-label="Excluir canal definitivamente"
              title="Excluir canal e histórico de mensagens"
            >
              <Trash2 size={14} className="text-danger-500" />
            </Button>
          </>
        )}
      </footer>

      {showRestartModal && (
        <QrPairModal
          numberId={channel.id}
          onClose={() => setShowRestartModal(false)}
          mode="restart"
        />
      )}
      {showPairModal && (
        <QrPairModal numberId={channel.id} onClose={() => setShowPairModal(false)} />
      )}
      {showRenameModal && (
        <ChannelGeneralSettingsModal channel={channel} onClose={() => setShowRenameModal(false)} />
      )}
    </article>
  );
}

/** Modal de pareamento — abre direto do card, sem trocar de página. */
function QrPairModal({
  numberId,
  onClose,
  mode = 'pair',
}: {
  numberId: number;
  onClose: () => void;
  mode?: 'pair' | 'restart';
}) {
  const queryClient = useQueryClient();
  const handleClose = () => {
    void queryClient.invalidateQueries({ queryKey: ['channels'] });
    void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    onClose();
  };
  useEscapeClose(true, handleClose);
  const title = mode === 'restart' ? 'Reiniciando canal' : 'Conectar via QR Code';
  const description =
    mode === 'restart'
      ? 'Reconectando com a identidade salva — sem necessidade de novo QR.'
      : 'Canal não-oficial via WhatsApp Web.';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-lg">
        <h2 className="mb-1 font-bold text-[16px] text-n-900">{title}</h2>
        <p className="mb-4 text-[12px] text-n-500">{description}</p>
        <QrPairingView numberId={numberId} onClose={handleClose} closeLabel="Fechar" mode={mode} />
      </div>
    </div>
  );
}

/** Formatador local — DD/MM/YYYY HH:mm em pt-BR. */
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusChip({ status }: { status: WhatsappNumber['status'] }) {
  const map: Record<WhatsappNumber['status'], { label: string; classes: string }> = {
    active: { label: 'Conectado', classes: 'bg-success-50 text-success-700' },
    pending: { label: 'Verificando', classes: 'bg-warning-50 text-warning-700' },
    unregistered: { label: 'Desconectado', classes: 'bg-n-100 text-n-600' },
    inactive: { label: 'Pausado', classes: 'bg-n-100 text-n-600' },
    pending_qr: { label: 'Aguardando QR', classes: 'bg-warning-50 text-warning-700' },
    revoked: { label: 'Revogado', classes: 'bg-danger-50 text-danger-700' },
  };
  const { label, classes } = map[status];
  const dotColor =
    status === 'active'
      ? 'bg-success-500'
      : status === 'inactive'
        ? 'bg-n-400'
        : status === 'revoked'
          ? 'bg-danger-500'
          : 'bg-warning-500';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.05em]',
        classes,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dotColor)} aria-hidden="true" />
      {label}
    </span>
  );
}

function Stat({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-n-500">
        {label}
      </span>
      <span className={cn('font-bold text-n-900 tabular-nums text-[18px]', valueClassName)}>
        {value}
      </span>
    </div>
  );
}

function tierLabel(tier: string | null): string {
  if (!tier) return '—';
  const map: Record<string, string> = {
    TIER_50: '50/dia',
    TIER_250: '250/dia',
    TIER_1K: '1K/dia',
    TIER_10K: '10K/dia',
    TIER_100K: '100K/dia',
    UNLIMITED: 'Ilimitado',
  };
  return map[tier] ?? tier;
}

/**
 * Modal "Adicionar Canal" — unifica os 3 fluxos antigos (manual, QR, Embedded
 * Signup) em entrada única. Usuário digita nome + escolhe tipo, e o parent
 * dispatcha pro fluxo correspondente.
 */
/** Tipos do "Adicionar Canal", na ordem do seletor. `oficial` = API oficial
 *  (Meta direto, BSP 360dialog, Instagram oficial) — os únicos que aparecem
 *  pra empresa "somente canais oficiais". */
const TIPOS_DE_CANAL: { kind: ChannelKind; label: string; oficial: boolean }[] = [
  { kind: 'whatsapp_web', label: 'Qr Code API', oficial: false },
  { kind: 'whatsapp_web_v2', label: 'Qr Code API V2', oficial: false },
  { kind: 'whatsapp_business_api', label: 'WhatsApp Business API', oficial: true },
  { kind: 'cloud_dev_app', label: 'API Cloud (Dev App)', oficial: false },
  { kind: 'dialog360', label: '360dialog (BSP)', oficial: true },
  { kind: 'instagram_meta', label: 'Instagram Direct (API oficial)', oficial: true },
  { kind: 'instagram_wame', label: 'Instagram Direct (WAME)', oficial: false },
];

function AddChannelModal({
  officialOnly,
  onClose,
  onConfirm,
}: {
  officialOnly: boolean;
  onClose: () => void;
  onConfirm: (name: string, kind: ChannelKind) => void;
}) {
  useEscapeClose(true, onClose);
  const tipos = officialOnly ? TIPOS_DE_CANAL.filter((t) => t.oficial) : TIPOS_DE_CANAL;
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ChannelKind>(tipos[0]?.kind ?? 'whatsapp_web');

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-channel-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-n-200 bg-n-0 p-6 shadow-2xl">
        <h2 id="add-channel-title" className="text-lg font-bold text-n-900">
          Adicionar canal
        </h2>
        <p className="mt-1 text-sm text-n-500">
          Dê um nome interno pro canal e escolha o tipo de conexão.
        </p>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) onConfirm(trimmed, kind);
          }}
        >
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-n-500">
              Nome do canal *
            </span>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Atendimento Vendas"
              maxLength={60}
              required
              autoFocus
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-n-500">
              Tipo
            </span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ChannelKind)}
              className="block w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-800 outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              {tipos.map((t) => (
                <option key={t.kind} value={t.kind}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              Adicionar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManualConnectModal({
  onClose,
  initialName,
}: {
  onClose: () => void;
  initialName?: string;
}) {
  useEscapeClose(true, onClose);
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    wabaId: '',
    phoneNumberId: '',
    accessToken: '',
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: connectManual,
    onSuccess: async (result) => {
      const name = initialName?.trim();
      if (name) {
        try {
          await renameChannel(result.whatsapp_number_id, name);
        } catch {
          // rename é best-effort
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'CHANNEL_LIMIT_REACHED') {
          toast.error(err.problem.title, {
            description: 'Fale com o suporte para ampliar o plano.',
          });
        }
        setError(
          err.code === 'WABA_ALREADY_CONNECTED'
            ? 'Esta WABA já está conectada a este tenant.'
            : err.problem.title,
        );
      } else {
        setError('Não foi possível conectar. Verifique os dados e tente de novo.');
      }
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manual-connect-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-lg">
        <h2 id="manual-connect-title" className="text-lg font-bold text-n-900">
          Conectar número manualmente
        </h2>
        <p className="mt-1 text-sm text-n-500">
          Reaproveite uma conexão já configurada no Meta WhatsApp Manager. O ChatPlus inscreve o app
          na sua WABA pra começar a receber webhooks.
        </p>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate(form);
          }}
        >
          <Field
            label="WABA ID"
            value={form.wabaId}
            onChange={(v) => setForm({ ...form, wabaId: v })}
          />
          <Field
            label="Phone Number ID"
            value={form.phoneNumberId}
            onChange={(v) => setForm({ ...form, phoneNumberId: v })}
          />
          <Field
            label="Access token"
            value={form.accessToken}
            onChange={(v) => setForm({ ...form, accessToken: v })}
            type="password"
          />
          {error && (
            <div role="alert" className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-600">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Conectando...' : 'Conectar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Conectar canal via BSP 360dialog: cola a D360-API-KEY → cria o canal → mostra
 * a URL de webhook pra o cliente colar no Hub do 360dialog (Webhooks → Callback
 * URL). O 360dialog não tem API pra setar o webhook, então essa etapa é manual.
 */
function Dialog360ConnectModal({
  onClose,
  initialName,
}: {
  onClose: () => void;
  initialName?: string;
}) {
  useEscapeClose(true, onClose);
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      connectDialog360({ label: (initialName ?? '').trim() || 'Canal 360dialog', apiKey }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      if (result.webhook_configured) {
        // Webhook já setado via API — nada mais a fazer, só a chave bastou.
        toast.success('Canal 360dialog conectado!');
        onClose();
      } else {
        // Fallback: não conseguimos configurar o webhook — mostra a URL pra colar.
        setWebhookUrl(result.webhook_url);
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'CHANNEL_LIMIT_REACHED') {
          toast.error(err.problem.title, {
            description: 'Fale com o suporte para ampliar o plano.',
          });
        }
        setError(err.problem.title);
      } else {
        setError('Não foi possível conectar. Verifique a chave e tente de novo.');
      }
    },
  });

  const copyUrl = (): void => {
    if (!webhookUrl) return;
    void navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog360-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-lg">
        <h2 id="dialog360-title" className="text-lg font-bold text-n-900">
          Conectar via 360dialog
        </h2>

        {!webhookUrl ? (
          <>
            <p className="mt-1 text-sm text-n-500">
              Cole a <strong>API Key</strong> do número (360dialog Hub → API Settings). Só isso — o
              webhook e o número são configurados automaticamente.
            </p>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                mutation.mutate();
              }}
            >
              <Field label="D360-API-KEY" value={apiKey} onChange={setApiKey} type="password" />
              {error && (
                <div
                  role="alert"
                  className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-600"
                >
                  {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={mutation.isPending || apiKey.trim().length === 0}>
                  {mutation.isPending ? 'Conectando…' : 'Conectar'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-n-500">
              Canal criado, mas <strong>não consegui configurar o webhook automaticamente</strong>.
              Copie a URL abaixo e cole no 360dialog Hub em <em>Webhooks → Callback URL</em>. Sem
              isso as mensagens não chegam.
            </p>
            <div className="mt-4">
              <WebhookCopyField
                label="URL de webhook (colar no 360dialog)"
                value={webhookUrl}
                loading={false}
                copied={copied}
                onCopy={copyUrl}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button type="button" onClick={onClose}>
                Concluir
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Card do canal Instagram (via parceiro WAME). Sem qualidade/limite/migração —
 * nada disso existe no Instagram. Inativar pausa o recebimento (não mexe na
 * WAME); a lixeira apaga o canal e o histórico.
 */
function InstagramChannelCard({ channel }: { channel: WhatsappNumber }) {
  const queryClient = useQueryClient();
  const [showSettings, setShowSettings] = useState(false);
  const isInactive = channel.status === 'inactive';
  const viaWame = channel.bsp === 'wame';
  // WAME: antes do 1º webhook o canal ainda não aprendeu o id da instância —
  // não recebeu nada ainda (normal logo após conectar). Na API oficial o id
  // da conta já vem na conexão.
  const aguardando = viaWame && !channel.phoneNumberId;
  // API oficial: desconectado = canal inativo ou token revogado/vencido (o
  // Desconectar apaga o token; a renovação automática marca revogado se a
  // Meta recusar). Nos dois casos o caminho é o mesmo: Reconectar.
  const desconectado =
    !viaWame &&
    (isInactive || channel.accountStatus === 'revoked' || channel.accountStatus === 'expired');

  const disconnectMut = useMutation({
    mutationFn: () => disconnectNumber(channel.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
    onError: (err) => {
      toast.error('Não foi possível desconectar', {
        description: err instanceof ApiError ? err.problem.title : undefined,
      });
    },
  });

  // Reconectar: login do Instagram amarrado a ESTE canal — a API recusa se a
  // conta do login for outra (não cria canal novo nem mistura históricos).
  const reconnectMut = useMutation({
    mutationFn: async () => {
      const token = getCurrentAccessToken();
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');
      return openInstagramLogin(token, channel.displayName ?? 'Instagram', channel.id);
    },
    onSuccess: (out) => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      if (out.status === 'connected') {
        toast.success(`Instagram @${out.result.username} reconectado!`);
      }
    },
    onError: (err) => {
      if (err instanceof InstagramLoginCancelledError) return;
      toast.error('Não foi possível reconectar', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const statusMut = useMutation({
    mutationFn: (status: 'active' | 'inactive') => setNumberStatus(channel.id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });
  const purgeMut = useMutation({
    mutationFn: () => purgeMetaChannel(channel.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
  });

  return (
    <article className="flex flex-col gap-3.5 rounded-md border border-n-200 bg-n-0 p-5 shadow-sm">
      <header className="flex items-center gap-3.5">
        <div
          className={cn(
            'flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-md',
            isInactive ? 'bg-n-300' : 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
          )}
          title="Instagram Direct"
        >
          <InstagramIcon size={26} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-[15px] font-semibold text-n-900">
              {channel.displayName ?? `Instagram #${channel.id}`}
            </div>
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="text-n-400 hover:text-n-700"
              title="Renomear canal"
              aria-label="Renomear canal"
            >
              <Pencil size={13} />
            </button>
            <span className="rounded-md bg-[#FCE7F3] px-1.5 py-0.5 text-[10px] font-semibold text-[#9D174D]">
              Instagram
            </span>
          </div>
          <div className="mt-0.5 font-mono text-xs text-n-500">
            <span
              className="mr-1.5 rounded bg-n-100 px-1.5 py-0.5 text-[11px] text-n-500"
              title="ID do Canal"
            >
              #{channel.id}
            </span>
            {viaWame
              ? 'Direct · via WAME'
              : `${channel.accountHandle ? `@${channel.accountHandle} · ` : ''}API oficial`}
          </div>
        </div>
        <StatusChip status={channel.status} />
      </header>

      {showSettings && (
        <ChannelGeneralSettingsModal channel={channel} onClose={() => setShowSettings(false)} />
      )}

      {aguardando && !isInactive && (
        <div className="flex items-center gap-1.5 rounded-md bg-n-50 px-2.5 py-1.5 text-[11px] text-n-600">
          Aguardando a primeira mensagem — mande um Direct pra conta pra confirmar a conexão.
        </div>
      )}

      {/* Mesmo aviso do card do WhatsApp oficial ("Token Meta revogado"): o
          Desconectar apaga o acesso — o chip fica "Pausado" nos dois. */}
      {desconectado && (
        <div className="flex items-center gap-1.5 rounded-md bg-danger-50 px-2.5 py-1.5 text-[11px] text-danger-700">
          <AlertTriangle size={12} />
          Acesso ao Instagram revogado — reconecte pra voltar a enviar e receber.
        </div>
      )}

      <footer className="flex gap-2 border-t border-n-200 pt-3.5">
        {!viaWame ? (
          // ─── API oficial — Desconectar/Reconectar (igual ao WhatsApp oficial) ───
          desconectado ? (
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => reconnectMut.mutate()}
              disabled={reconnectMut.isPending}
              title="Entrar de novo com a conta do Instagram (mantém o histórico)"
            >
              <Plug aria-hidden="true" />
              {reconnectMut.isPending ? 'Aguardando o Instagram…' : 'Reconectar'}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="flex-1"
              onClick={async () => {
                const ok = await confirm({
                  title: `Desconectar ${channel.displayName ?? `canal #${channel.id}`}?`,
                  description:
                    'As mensagens do Direct param de chegar no ChatPlus. Histórico, tickets e contatos são preservados. Pra voltar, use Reconectar e entre de novo com a conta do Instagram.',
                  confirmText: 'Desconectar',
                  variant: 'danger',
                });
                if (ok) disconnectMut.mutate();
              }}
              disabled={disconnectMut.isPending}
              title="Desconecta o Instagram do ChatPlus (preserva o histórico)"
            >
              <Power aria-hidden="true" />
              Desconectar
            </Button>
          )
        ) : isInactive ? (
          <Button
            variant="primary"
            className="flex-1"
            onClick={() => statusMut.mutate('active')}
            disabled={statusMut.isPending}
          >
            <PlayCircle aria-hidden="true" />
            Reativar
          </Button>
        ) : (
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => statusMut.mutate('inactive')}
            disabled={statusMut.isPending}
            title="Pausa o recebimento de mensagens"
          >
            <PauseCircle aria-hidden="true" />
            Inativar
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={async () => {
            const ok = await confirm({
              title: `Excluir ${channel.displayName ?? `canal #${channel.id}`}?`,
              description: `Remove o canal + TODO o histórico de mensagens permanentemente. Contatos são preservados. ${
                viaWame
                  ? 'A conta do Instagram continua conectada na WAME.'
                  : 'O app continua autorizado na conta do Instagram (dá pra remover no Instagram, em Apps e sites).'
              } Esta ação não pode ser desfeita.`,
              confirmText: 'Excluir definitivamente',
              variant: 'danger',
              requireText: 'EXCLUIR',
            });
            if (ok) purgeMut.mutate();
          }}
          disabled={purgeMut.isPending}
          aria-label="Excluir canal definitivamente"
          title="Excluir canal e histórico de mensagens"
        >
          <Trash2 size={14} className="text-danger-500" />
        </Button>
      </footer>
    </article>
  );
}

/**
 * Conectar Instagram Direct via parceiro WAME: a conta do Instagram já foi
 * conectada no painel da WAME; aqui o cliente cola Server + Key da instância.
 * O webhook é apontado automaticamente — se a instância já mandava mensagens
 * pra OUTRO sistema, pergunta antes de tirar dele.
 *
 * Não fecha no clique fora (a Key colada se perderia) — só Esc ou Cancelar.
 */
function InstagramConnectModal({
  onClose,
  initialName,
}: {
  onClose: () => void;
  initialName?: string;
}) {
  useEscapeClose(true, onClose);
  const queryClient = useQueryClient();
  const [server, setServer] = useState('https://us.api-wa.me');
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: (confirmOverwrite: boolean) =>
      connectWame({
        label: (initialName ?? '').trim() || 'Instagram',
        server: server.trim(),
        key: key.trim(),
        confirmOverwrite,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      if (result.webhook_configured) {
        toast.success('Instagram conectado!', {
          description: 'Mande um Direct pra conta pra confirmar que as mensagens estão chegando.',
        });
        onClose();
      } else {
        setWebhookUrl(result.webhook_url);
      }
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.code === 'WAME_WEBHOOK_IN_USE') {
        const atual = String(err.problem.context?.current_webhook ?? '');
        const ok = await confirm({
          title: 'Esta instância já manda as mensagens pra outro sistema',
          description: `Hoje as mensagens vão para: ${atual}\n\nSe conectar aqui, esse sistema PARA de receber as mensagens desta instância (WhatsApp e Messenger dela também, se houver).`,
          confirmText: 'Conectar mesmo assim',
          variant: 'danger',
        });
        if (ok) mutation.mutate(true);
        return;
      }
      if (err instanceof ApiError) {
        if (err.code === 'CHANNEL_LIMIT_REACHED') {
          toast.error(err.problem.title, {
            description: 'Fale com o suporte para ampliar o plano.',
          });
        }
        setError(
          err.problem.detail ? `${err.problem.title} — ${err.problem.detail}` : err.problem.title,
        );
      } else {
        setError('Não foi possível conectar. Confira o Server e a Key e tente de novo.');
      }
    },
  });

  const copyUrl = (): void => {
    if (!webhookUrl) return;
    void navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="instagram-connect-title"
    >
      <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-lg">
        <h2 id="instagram-connect-title" className="text-lg font-bold text-n-900">
          Conectar Instagram Direct
        </h2>

        {!webhookUrl ? (
          <>
            <p className="mt-1 text-sm text-n-500">
              Conecte a conta do Instagram no painel da <strong>WAME</strong> e cole aqui o{' '}
              <strong>Server</strong> e a <strong>Key</strong> da instância. O recebimento das
              mensagens é configurado automaticamente.
            </p>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                mutation.mutate(false);
              }}
            >
              <Field label="Server" value={server} onChange={setServer} />
              <Field label="Key" value={key} onChange={setKey} type="password" />
              {error && (
                <div
                  role="alert"
                  className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-600"
                >
                  {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={
                    mutation.isPending || key.trim().length === 0 || server.trim().length === 0
                  }
                >
                  {mutation.isPending ? 'Conectando…' : 'Conectar'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-n-500">
              Canal criado, mas{' '}
              <strong>não consegui configurar o recebimento automaticamente</strong>. Cole a URL
              abaixo no painel da WAME, no webhook de mensagens da instância (formato <em>meta</em>
              ). Sem isso as mensagens não chegam.
            </p>
            <div className="mt-4">
              <WebhookCopyField
                label="URL de webhook (colar na WAME)"
                value={webhookUrl}
                loading={false}
                copied={copied}
                onCopy={copyUrl}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button type="button" onClick={onClose}>
                Concluir
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  maxLength?: number;
}

function Field({ label, value, onChange, type = 'text', placeholder, maxLength }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-n-500">
        {label}
      </span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        required
      />
    </label>
  );
}

/** Modal de edição de apelido interno do canal — funciona pra qualquer channelType. */
function RenameChannelModal({
  channelId,
  currentName,
  onClose,
}: {
  channelId: number;
  currentName: string | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const queryClient = useQueryClient();
  const [name, setName] = useState(currentName ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => renameChannel(channelId, name.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.title : 'Erro ao renomear');
    },
  });

  const trimmed = name.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 60 && trimmed !== currentName;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      // Sem fechar no clique fora — só Esc (useEscapeClose) ou Cancelar.
    >
      <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-lg">
        <h2 className="mb-1 font-bold text-[16px] text-n-900">Renomear canal</h2>
        <p className="mb-4 text-[12px] text-n-500">
          Apelido interno do ChatPlus — não é enviado pra Meta nem aparece pros contatos.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            setError(null);
            mutation.mutate();
          }}
          className="space-y-4"
        >
          <Field
            label="Apelido"
            value={name}
            onChange={setName}
            placeholder="WhatsApp Suporte"
            maxLength={60}
          />
          {error && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !valid}>
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Modal de configurações do canal meta_official.
 * Seção 1: webhook info (URL + verify token) copiáveis — para configurar no Meta Developer Console.
 * Seção 2: credenciais editáveis (WABA ID, Phone Number ID, número, token, PIN).
 */
function ChannelSettingsModal({
  channel,
  onClose,
}: {
  channel: WhatsappNumber;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    wabaId: channel.wabaId ?? '',
    phoneNumberId: channel.phoneNumberId ?? '',
    accessToken: '',
    pin: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'token' | null>(null);

  const { data: webhookInfo, isLoading: loadingWebhook } = useQuery({
    queryKey: ['webhook-info'],
    queryFn: getWebhookInfo,
    staleTime: 0,
  });

  const copyToClipboard = (text: string | null, field: 'url' | 'token') => {
    if (!text) return;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const mutation = useMutation({
    mutationFn: () =>
      updateChannelCredentials(channel.id, {
        wabaId: form.wabaId.trim() !== (channel.wabaId ?? '') ? form.wabaId.trim() : undefined,
        phoneNumberId:
          form.phoneNumberId.trim() !== (channel.phoneNumberId ?? '')
            ? form.phoneNumberId.trim()
            : undefined,
        accessToken: form.accessToken.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.title : 'Erro ao salvar. Tente novamente.');
    },
  });

  const hasChange =
    form.wabaId.trim() !== (channel.wabaId ?? '') ||
    form.phoneNumberId.trim() !== (channel.phoneNumberId ?? '') ||
    form.accessToken.trim() !== '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="channel-settings-title"
      // Sem fechar no clique fora — só Esc (useEscapeClose) ou Cancelar.
    >
      <div className="w-full max-w-md rounded-md bg-n-0 shadow-lg overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-n-200">
          <h2 id="channel-settings-title" className="font-bold text-[16px] text-n-900">
            Configurações do canal
          </h2>
          <p className="mt-1 text-[12px] text-n-500">
            {channel.displayName ?? channel.displayNumber ?? `Canal #${channel.id}`}
          </p>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Seção 1: Webhook info */}
          <div>
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-n-500">
              Configuração de Webhook Meta
            </h3>
            <p className="mb-3 text-[11px] text-n-400">
              Cole estes valores no painel{' '}
              <span className="font-medium">
                Meta Developer Console → WhatsApp → Configuration → Webhooks
              </span>
              .
            </p>
            <div className="space-y-2">
              <WebhookCopyField
                label="URL do Webhook"
                value={
                  loadingWebhook
                    ? null
                    : webhookInfo?.webhookUrl
                      ? `${webhookInfo.webhookUrl}/${channel.id}`
                      : null
                }
                loading={loadingWebhook}
                copied={copied === 'url'}
                onCopy={() =>
                  copyToClipboard(
                    webhookInfo?.webhookUrl ? `${webhookInfo.webhookUrl}/${channel.id}` : null,
                    'url',
                  )
                }
              />
              <WebhookCopyField
                label="Token de Verificação"
                value={loadingWebhook ? null : (webhookInfo?.verifyToken ?? null)}
                loading={loadingWebhook}
                copied={copied === 'token'}
                onCopy={() => copyToClipboard(webhookInfo?.verifyToken ?? null, 'token')}
              />
            </div>
          </div>

          {/* Seção 2: Credenciais editáveis */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              mutation.mutate();
            }}
          >
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-n-500">
              Credenciais do canal
            </h3>
            <div className="space-y-3">
              <Field
                label="WABA ID"
                value={form.wabaId}
                onChange={(v) => setForm({ ...form, wabaId: v })}
              />
              <Field
                label="Phone Number ID"
                value={form.phoneNumberId}
                onChange={(v) => setForm({ ...form, phoneNumberId: v })}
              />
              <div>
                <Field
                  label="Novo access token"
                  value={form.accessToken}
                  onChange={(v) => setForm({ ...form, accessToken: v })}
                  type="password"
                  placeholder="Deixe em branco para não alterar"
                />
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-600"
              >
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending || !hasChange}>
                {mutation.isPending ? 'Salvando...' : 'Salvar alterações'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function WebhookCopyField({
  label,
  value,
  loading,
  copied,
  onCopy,
}: {
  label: string;
  value: string | null;
  loading: boolean;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div>
      <span className="block mb-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-n-500">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <div className="flex-1 rounded-md border border-n-200 bg-n-50 px-3 py-1.5 font-mono text-[11px] text-n-800 truncate min-h-[30px] flex items-center">
          {loading ? (
            <span className="text-n-400 italic">Carregando...</span>
          ) : value ? (
            value
          ) : (
            <span className="text-n-400 italic">Não configurado</span>
          )}
        </div>
        <button
          type="button"
          onClick={onCopy}
          disabled={!value || loading}
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md border border-n-200 bg-n-0 text-n-600 hover:bg-n-100 disabled:opacity-40"
          title="Copiar"
          aria-label={`Copiar ${label}`}
        >
          {copied ? <Check size={13} className="text-success-600" /> : <Copy size={13} />}
        </button>
      </div>
    </div>
  );
}

// Modal wizard pra conectar canal qr_code (whatsmeow) — 2 stages no mesmo
// modal: (1) digita apelido, (2) renderiza QR pra escanear. Sem redirect.
// Se `initialName` vier preenchido (caminho via AddChannelModal), pula direto
// pra criação + stage QR.
function QrConnectModal({
  onClose,
  initialName,
  provider = 'whatsmeow',
}: {
  onClose: () => void;
  initialName?: string;
  /** Motor do canal: 'whatsmeow' (Qr Code API) ou 'zapo' (QR CODE V2). */
  provider?: 'whatsmeow' | 'zapo';
}) {
  useEscapeClose(true, onClose);
  const queryClient = useQueryClient();
  const initialLabel = initialName?.trim() ?? '';
  const [stage, setStage] = useState<'label' | 'qr'>(initialLabel ? 'qr' : 'label');
  const [createdNumberId, setCreatedNumberId] = useState<number | null>(null);
  const [label, setLabel] = useState(initialLabel);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createQrChannel,
    onSuccess: (result) => {
      setCreatedNumberId(result.whatsapp_number_id);
      setStage('qr');
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    },
    onError: (err) => {
      // Limite do plano: toast padrão (o pré-check no botão cobre o caso
      // comum; aqui é o backstop quando o uso mudou entre o clique e o create).
      if (err instanceof ApiError && err.code === 'CHANNEL_LIMIT_REACHED') {
        toast.error(err.problem.title, {
          description: 'Fale com o suporte para ampliar o plano.',
        });
      }
      setError(err instanceof ApiError ? err.problem.title : 'Erro ao criar canal QR');
    },
  });

  // Dispara create automaticamente quando vier com initialName
  // (skipa stage 'label' que o AddChannelModal já cobriu)
  const didAutoCreateRef = useRef(false);
  useEffect(() => {
    if (initialLabel && !didAutoCreateRef.current && createdNumberId === null) {
      didAutoCreateRef.current = true;
      mutation.mutate({ label: initialLabel, provider });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    void queryClient.invalidateQueries({ queryKey: ['channels'] });
    void queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !mutation.isPending) handleClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-lg">
        <h2 className="mb-1 font-bold text-[16px] text-n-900">Conectar via QR Code</h2>
        <p className="mb-4 text-[12px] text-n-500">Canal não-oficial via WhatsApp Web.</p>

        {stage === 'label' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              mutation.mutate({ label, provider });
            }}
            className="space-y-4"
          >
            <Field
              label="Apelido do canal"
              value={label}
              onChange={setLabel}
              placeholder="WhatsApp Suporte"
              maxLength={60}
            />
            {error && (
              <p className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={mutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending || label.trim().length === 0}>
                {mutation.isPending ? 'Criando...' : 'Continuar'}
              </Button>
            </div>
          </form>
        )}

        {stage === 'qr' && createdNumberId !== null && (
          <QrPairingView numberId={createdNumberId} onClose={handleClose} closeLabel="Fechar" />
        )}
      </div>
    </div>
  );
}
